/**
 * The view-tabs bar (`specs/M4-V1-views.md` §8).
 *
 * Round 3 built this from `specs/M1-B1` §7.6 and, as round 5h's captures showed, invented three
 * things monday does not have: a *Set as default view* entry, a shared/personal toggle, and a
 * board-wide drag reorder. What monday actually offers is in `ViewTabMenu` — and its reorder is
 * explicitly **"for you only"**, so dragging a tab now writes per-user state and says so.
 *
 * Geometry is B1's, measured on a production board: a 42 px band, `All ⌄` 84 × 32 and `+`
 * 32 × 32. (The sandbox capture measures the `li[role=tab]` itself at 34 px inside that band.)
 */
import { Icon, icons, useConfirm, useToast } from '@elizmonday/ui';
import { personalViewOrder } from '@elizmonday/shared';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { AddViewGallery } from './AddViewGallery';
import { LockViewDialog } from './LockViewDialog';
import { ShareViewDialog, type ShareViewState } from './ShareViewDialog';
import { ViewTabMenu, type ViewTabMenuTarget } from './ViewTabMenu';
import { ViewSettingsPane, type ViewSettingsTarget } from './ViewSettingsPane';
import { usePhone } from '../board-view/table/phone/usePhoneLayout';
import { usePersonalViewOrder, useViewAdminMutations } from './hooks';
import { useBoardViews, useViewMutations } from '../boards/hooks';
import './views.css';

export interface ViewTabsBarProps {
  boardId: string;
  activeViewId: string | null;
  onSelectView(viewId: string): void;
}

export function ViewTabsBar({ boardId, activeViewId, onSelectView }: ViewTabsBarProps) {
  const views = useBoardViews(boardId);
  const order = usePersonalViewOrder(boardId);
  const mutations = useViewMutations(boardId);
  const admin = useViewAdminMutations(boardId);
  const confirm = useConfirm();
  const toast = useToast();

  const [menuFor, setMenuFor] = useState<{ viewId: string | null; x: number; y: number } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [galleryAt, setGalleryAt] = useState<{ x: number; y: number } | null>(null);
  const [allOpen, setAllOpen] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [lockFor, setLockFor] = useState<{ viewId: string; name: string; locked: boolean } | null>(null);
  const [shareFor, setShareFor] = useState<{ viewId: string; name: string } | null>(null);
  /** P-144: the view whose `[V1§8]` settings pane is open, if any. */
  const [settingsFor, setSettingsFor] = useState<string | null>(null);
  const [share, setShare] = useState<ShareViewState>({
    published: false, url: null, embedUrl: null, embedCode: null,
  });

  /**
   * **P-261.** On a phone the settings pane is a **bottom sheet** (`mobile.css` F9-D27, now on
   * `--ez-layer-sheet`), and a sheet yields to a menu twice over: the menu paints above it, and
   * the sheet closes so nothing of it is left under the fingers. On desktop the pane is a docked
   * 320 px column beside the board — it overlaps no menu and must stay put, so this is the one
   * behaviour in the bar that reads the viewport.
   */
  const phone = usePhone();
  const menuOpen = menuFor !== null || galleryAt !== null || allOpen;
  useEffect(() => {
    if (phone && menuOpen) setSettingsFor(null);
  }, [phone, menuOpen]);

  const all = views.data ?? [];
  /** Hidden views keep their row and their data; they are only absent from the bar (§4). */
  const tabs = useMemo(
    () => personalViewOrder(all.filter((view) => !view.hidden), order.data ?? {}),
    [all, order.data],
  );
  const selected = activeViewId ?? tabs.find((view) => view.isDefault)?.id ?? tabs[0]?.id ?? null;

  /**
   * P-042 §2. `.ez-view-tabs__list` was `overflow: hidden`, so a board with 70 views rendered a
   * **5,911 px** strip inside a 278 px box on an iPhone 14 and 67 of its views were unreachable
   * at any viewport. `mobile.css` turns the strip into a horizontal scroller on a phone; a
   * scroller starts at `scrollLeft: 0`, which would open a board on the first tab rather than on
   * the one it is showing, so the active tab is scrolled into view whenever it changes. On
   * desktop the strip does not scroll and `scrollIntoView` on an element already in view is a
   * no-op, so no captured geometry moves.
   */
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = listRef.current;
    if (!list || list.scrollWidth <= list.clientWidth + 1) return;
    const active = list.querySelector<HTMLElement>('[data-selected]');
    if (!active) return;
    const left = active.offsetLeft - Math.max(0, (list.clientWidth - active.offsetWidth) / 2);
    list.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
  }, [selected, tabs.length]);

  const targetFor = (viewId: string | null): ViewTabMenuTarget => {
    const view = all.find((entry) => entry.id === viewId);
    return {
      viewId,
      name: view?.name ?? 'this view',
      synthetic: view?.synthetic ?? viewId === null,
      locked: view?.locked ?? false,
      pinned: view?.pinned ?? false,
      published: (view?.publicToken ?? null) !== null,
      isDefault: view?.isDefault ?? false,
      // P-085: a personal view is nobody else's landing page, so it cannot be a board default.
      personal: view?.visibility === 'personal',
    };
  };

  /** P-144: the view row as `[V1§8]`'s pane reads it. */
  const settingsTargetFor = (viewId: string): ViewSettingsTarget => {
    const view = all.find((entry) => entry.id === viewId);
    return {
      viewId,
      name: view?.name ?? 'this view',
      registryId: view?.registryId ?? null,
      locked: view?.locked ?? false,
      pinned: view?.pinned ?? false,
      hidden: view?.hidden ?? false,
      visibility: view?.visibility ?? 'shared',
      isDefault: view?.isDefault ?? false,
      settings: (view?.settings ?? {}) as Record<string, unknown>,
    };
  };

  const openMenu = (viewId: string | null, element: HTMLElement) => {
    const box = element.getBoundingClientRect();
    setMenuFor({ viewId, x: box.left, y: box.bottom + 4 });
  };

  const onDrop = (event: DragEvent, targetId: string) => {
    event.preventDefault();
    if (!dragging || dragging === targetId) return;
    const from = tabs.findIndex((view) => view.id === dragging);
    const to = tabs.findIndex((view) => view.id === targetId);
    setDragging(null);
    if (from === -1 || to === -1) return;
    // "Reorder (for you only)": a drag is the same per-user write as the menu's Move entries.
    admin.reorderForMe.mutate({ viewId: dragging, method: to < from ? 'back' : 'ahead' });
    toast('Reordered for you only', { kind: 'normal' });
  };

  return (
    <nav className="ez-view-tabs" aria-label="Board views" data-testid="view-tabs">
      {/* P-145: `tools/qa/inventory.mjs` matches coverage by `data-testid`, and the strip a
          phone drags (`mobile.css`, P-042 §2) and the button a click switches view with both
          had none, so no outcome round could ever credit either. */}
      <div
        className="ez-view-tabs__list"
        ref={listRef}
        role="tablist"
        aria-label="Board views"
        data-testid="view-tabs-list"
      >
        {tabs.map((view) => {
          const isSelected = view.id === selected;
          return (
            <div
              key={view.id ?? view.name}
              className="ez-view-tabs__tab"
              data-selected={isSelected || undefined}
              data-pinned={view.pinned || undefined}
              data-locked={view.locked || undefined}
              data-testid={`view-tab-${view.id ?? 'implicit'}`}
              draggable={view.id !== null}
              onDragStart={() => setDragging(view.id)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => view.id !== null && onDrop(event, view.id)}
              // monday's own tooltip: "Use the right click to open the menu settings".
              onContextMenu={(event) => {
                event.preventDefault();
                setMenuFor({ viewId: view.id, x: event.clientX, y: event.clientY });
              }}
            >
              {renaming === view.id ? (
                <input
                  className="ez-view-tabs__rename"
                  aria-label={`Rename ${view.name}`}
                  defaultValue={view.name}
                  data-testid="view-rename-input"
                  autoFocus
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      const next = event.currentTarget.value.trim();
                      setRenaming(null);
                      if (next && next !== view.name && view.id) {
                        mutations.rename.mutate({ viewId: view.id, name: next });
                      }
                    }
                    if (event.key === 'Escape') setRenaming(null);
                  }}
                  onBlur={(event) => {
                    const next = event.target.value.trim();
                    setRenaming(null);
                    if (next && next !== view.name && view.id) {
                      mutations.rename.mutate({ viewId: view.id, name: next });
                    }
                  }}
                />
              ) : (
                <>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    className="ez-view-tabs__button"
                    data-testid={`view-tab-button-${view.id ?? 'implicit'}`}
                    onClick={() => view.id && onSelectView(view.id)}
                  >
                    {view.pinned ? (
                      <Icon icon={icons.pinFilled} size={14} ignoreFocusStyle data-testid="view-tab-pin" />
                    ) : null}
                    {view.name}
                    {view.locked ? (
                      <span className="ez-view-tabs__lock" data-testid="view-tab-lock" aria-label="Locked view">
                        🔒
                      </span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    className="ez-view-tabs__menu"
                    aria-label={`${view.name} options`}
                    data-testid={`view-menu-${view.id ?? 'implicit'}`}
                    onClick={(event) => openMenu(view.id, event.currentTarget)}
                  >
                    <Icon icon={icons.moreActions} size={16} ignoreFocusStyle />
                  </button>
                </>
              )}
            </div>
          );
        })}

      </div>

      {/*
        * Outside the `tablist`, and outside the strip it used to live in (round 9o, P-042 §2).
        * `All ⌄` is not a tab — it is the only way back to a *hidden* view — and at the end of a
        * 5,911 px scroller it could not be reached at all. Pinning it inside the scroller with
        * `position: sticky` worked and cost the tab underneath its taps, which is §8's own
        * warning about overlays stealing a neighbour's hits; a sibling of the strip overlaps
        * nothing. The flex row and its 4 px gap are unchanged, so the desktop bar does not move.
        */}
      <button
        type="button"
        className="ez-view-tabs__all"
        aria-expanded={allOpen}
        data-testid="view-tabs-all"
        onClick={() => setAllOpen((open) => !open)}
      >
        All
        <Icon icon={icons.chevronDown} size={16} ignoreFocusStyle />
      </button>

      {/* Outside the `tablist`: it adds a view, it is not one (A2). */}
      <button
        type="button"
        className="ez-view-tabs__add"
        aria-label="Add view"
        title="Add view"
        data-testid="view-tabs-add"
        onClick={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          setGalleryAt({ x: box.left, y: box.bottom + 4 });
        }}
      >
        <Icon icon={icons.add} size={16} ignoreFocusStyle />
      </button>

      {allOpen ? (
        /** `All ⌄` lists hidden views too — it is the only way back to one (acceptance V4). */
        <div className="ez-view-tabs__all-list" role="dialog" aria-label="All views" data-testid="view-all-list">
          {all.map((view) => (
            <button
              key={view.id ?? view.name}
              type="button"
              data-testid={`view-all-${view.id ?? 'implicit'}`}
              data-hidden={view.hidden || undefined}
              onClick={() => {
                setAllOpen(false);
                if (view.id) onSelectView(view.id);
              }}
            >
              {view.name}
              {view.hidden ? <span className="ez-view-tabs__hidden-badge">Hidden</span> : null}
            </button>
          ))}
        </div>
      ) : null}

      {menuFor ? (
        <ViewTabMenu
          target={targetFor(menuFor.viewId)}
          x={menuFor.x}
          y={menuFor.y}
          onClose={() => setMenuFor(null)}
          actions={{
            onPin: (pinned) => {
              setMenuFor(null);
              if (menuFor.viewId) admin.pin.mutate({ viewId: menuFor.viewId, pinned });
            },
            /** P-085: `views.setDefault` — the view `/boards/:id` resolves to. */
            onSetDefault: () => {
              const target = targetFor(menuFor.viewId);
              setMenuFor(null);
              if (menuFor.viewId === null || target.isDefault) return;
              mutations.setDefault.mutate(menuFor.viewId, {
                onSuccess: () => toast(`${target.name} is now the default view`, { kind: 'positive' }),
              });
            },
            onRename: () => {
              setMenuFor(null);
              setRenaming(menuFor.viewId);
            },
            onDuplicate: () => {
              setMenuFor(null);
              if (menuFor.viewId) mutations.duplicate.mutate(menuFor.viewId);
            },
            onShare: () => {
              const target = targetFor(menuFor.viewId);
              setMenuFor(null);
              if (menuFor.viewId === null) return;
              setShareFor({ viewId: menuFor.viewId, name: target.name });
              setShare({ published: target.published, url: null, embedUrl: null, embedCode: null });
            },
            onLock: () => {
              const target = targetFor(menuFor.viewId);
              setMenuFor(null);
              if (menuFor.viewId === null) return;
              setLockFor({ viewId: menuFor.viewId, name: target.name, locked: target.locked });
            },
            /** P-144: `[V1§8]`'s pane, on the view the menu belongs to. */
            onSettings: () => {
              const viewId = menuFor.viewId;
              setMenuFor(null);
              if (viewId !== null) setSettingsFor(viewId);
            },
            /**
             * P-153. `viewsAdmin.hide` keeps the row and its data — the tab simply leaves the
             * bar — and `All ⌄` is the way back, which the toast says because a control that
             * makes something disappear must say where it went.
             */
            onHide: () => {
              const target = targetFor(menuFor.viewId);
              setMenuFor(null);
              admin.hide.mutate(
                { viewId: menuFor.viewId, hidden: true },
                {
                  onSuccess: () =>
                    toast(`${target.name} is hidden. Find it under All.`, {
                      kind: 'normal',
                      action: {
                        label: 'Undo',
                        onClick: () => admin.hide.mutate({ viewId: menuFor.viewId, hidden: false }),
                      },
                    }),
                },
              );
            },
            onReorder: (method) => {
              setMenuFor(null);
              if (menuFor.viewId) admin.reorderForMe.mutate({ viewId: menuFor.viewId, method });
            },
            onDelete: () => {
              const target = targetFor(menuFor.viewId);
              setMenuFor(null);
              if (menuFor.viewId === null) return;
              void (async () => {
                const ok = await confirm({
                  title: `Delete ${target.name}?`,
                  body: 'The view is removed from the bar. Its filters and sorts go with it.',
                  confirmLabel: 'Delete',
                  danger: true,
                });
                if (!ok) return;
                const deletedId = menuFor.viewId!;
                await mutations.remove.mutateAsync(deletedId);
                /*
                 * P-151. `views.remove` is **soft** and `views.restore` puts the row back with
                 * its filters, its sorts and its settings, writing `board_view_restored` — so a
                 * destructive action the product already knows how to reverse offers the
                 * reversal, exactly as the grid's own delete toast does. What monday puts on
                 * this toast is q505; offering the undo is the conservative reading.
                 */
                toast('View deleted', {
                  kind: 'positive',
                  action: {
                    label: 'Undo',
                    onClick: () => {
                      mutations.restore.mutate(deletedId, {
                        onSuccess: () => onSelectView(deletedId),
                      });
                    },
                  },
                });
              })();
            },
          }}
        />
      ) : null}

      {lockFor ? (
        <LockViewDialog
          name={lockFor.name}
          locked={lockFor.locked}
          onClose={() => setLockFor(null)}
          onConfirm={() => {
            admin.lock.mutate({ viewId: lockFor.viewId, locked: !lockFor.locked });
            setLockFor(null);
          }}
        />
      ) : null}

      {shareFor ? (
        <ShareViewDialog
          name={shareFor.name}
          state={share}
          onClose={() => setShareFor(null)}
          onPublish={() => {
            void (async () => {
              const result = await admin.publish.mutateAsync({ viewId: shareFor.viewId });
              setShare({ published: true, url: result.url, embedUrl: result.embedUrl, embedCode: result.embedCode });
            })();
          }}
          onRegenerate={() => {
            void (async () => {
              const result = await admin.publish.mutateAsync({ viewId: shareFor.viewId, regenerate: true });
              setShare({ published: true, url: result.url, embedUrl: result.embedUrl, embedCode: result.embedCode });
            })();
          }}
          onStop={() => {
            admin.unpublish.mutate(shareFor.viewId);
            setShare({ published: false, url: null, embedUrl: null, embedCode: null });
          }}
        />
      ) : null}

      {settingsFor === null ? null : (
        /**
         * **P-144.** `[V1§8]`'s pane, which until this round was exported and rendered by
         * nothing. It is a *read* of the view row plus the rename the pane's Name field owns;
         * a **locked** view renders read-only with monday's own sentence, because whether the
         * signed-in user may edit a locked view is a board-owner question this bar cannot
         * answer (F8 §5) and the conservative answer is "not from here".
         */
        <ViewSettingsPane
          target={settingsTargetFor(settingsFor)}
          canEdit={false}
          onRename={(name) => mutations.rename.mutate({ viewId: settingsFor, name })}
          onClose={() => setSettingsFor(null)}
        />
      )}

      {galleryAt ? (
        <AddViewGallery
          x={galleryAt.x}
          y={galleryAt.y}
          onClose={() => setGalleryAt(null)}
          onCreate={(registryId) => {
            setGalleryAt(null);
            void (async () => {
              const view = await mutations.create.mutateAsync({ registryId });
              if (view?.id) onSelectView(String(view.id));
            })();
          }}
        />
      ) : null}
    </nav>
  );
}
